import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface PageContent {
  title: string;
  content: string;
  description?: string;
  heroImage?: string;
  images?: string[];
  schedule?: {
    day: string;
    time: string;
  };
  location?: string;
  details?: string;
  contactEmail?: string;
  contactPhone?: string;
}

interface HomepageData {
  hero?: {
    title: string;
    subtitle: string;
    buttonText: string;
    secondaryButtonText: string;
  };
  logo?: string;
  stats?: {
    members: string;
    activities: string;
    yearEstablished: string;
    hubCapacity: string;
  };
  sections?: {
    membershipHeading: string;
    membershipText: string;
    hubHeading: string;
    hubText: string;
    activitiesHeading: string;
  };
  contact?: {
    address: string;
  };
}

interface CommunityHubData {
  hero?: {
    title: string;
    subtitle: string;
    buttonText: string;
    secondaryButtonText: string;
  };
  description?: string;
  features?: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  pricing?: {
    showPricing: boolean;
    depositAmount: number;
    currency: string;
  };
  images?: string[];
  gallery?: string[];
}

const PageEditor: React.FC = () => {
  const [selectedPage, setSelectedPage] = useState<string>('home');
  const [pageContent, setPageContent] = useState<PageContent>({ 
    title: '', 
    content: '',
    description: '',
    heroImage: '',
    images: []
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const [footerContent, setFooterContent] = useState<any>(null);
  const [homepageData, setHomepageData] = useState<HomepageData | null>(null);
  const [communityHubData, setCommunityHubData] = useState<CommunityHubData | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);

  const pages = [
    { value: 'home', label: '🏠 Home Page', category: 'main' },
    { value: 'community-hub', label: '🏢 Community Hub', category: 'main' },
    { value: 'footer', label: '📋 Footer (All Pages)', category: 'global' },
    { value: 'football', label: '⚽ Football', category: 'activity' },
    { value: 'walkAndTalk', label: '🚶 Walk and Talk', category: 'activity' },
    { value: 'womensCoffeeMornings', label: '☕ Women\'s Coffee Morning', category: 'activity' },
    { value: 'womensAerobics', label: '💪 Women\'s Aerobics', category: 'activity' },
    { value: 'cycling', label: '🚴 Cycling', category: 'activity' }
  ];

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  useEffect(() => {
    loadPageContent();
  }, [selectedPage]);

  const loadPageContent = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('adminToken');
      let response;

      if (selectedPage === 'home') {
        // Load homepage settings
        response = await axios.get(`http://localhost:5000/api/homepage`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data.data || response.data;
        
        setHomepageData({
          hero: {
            title: data.hero?.title || data.heroTitle || 'Welcome to Leftley Sports Club',
            subtitle: data.hero?.subtitle || data.heroSubtitle || 'Building a stronger community through sports and activities since 2016',
            buttonText: data.hero?.buttonText || 'Become a Member',
            secondaryButtonText: data.hero?.secondaryButtonText || 'Hire the Hub'
          },
          logo: data.logo || '',
          stats: {
            members: data.stats?.members || '500+',
            activities: data.stats?.activities || '5+',
            yearEstablished: data.stats?.yearEstablished || 'Since 2016',
            hubCapacity: data.stats?.hubCapacity || '80'
          },
          sections: {
            membershipHeading: data.sections?.membershipHeading || 'Become a Member',
            membershipText: data.sections?.membershipText || 'Join our community of 500+ members and get access to all our sports and activities.',
            hubHeading: data.sections?.hubHeading || 'Hire Our Community Hub',
            hubText: data.sections?.hubText || 'Our community hub is perfect for events, meetings, and celebrations.',
            activitiesHeading: data.sections?.activitiesHeading || 'Our Activities'
          },
          contact: {
            address: data.contact?.address || 'LSC Community Hub, Martin Peters Court, Meadow Rd, Barking IG11 9FE'
          }
        });
        
        setPageContent({ title: 'Homepage', content: '', description: '', images: [] });
      } else if (selectedPage === 'footer') {
        // Load footer settings
        response = await axios.get(`http://localhost:5000/api/homepage`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data.data || response.data;
        setFooterContent({
          aboutText: data.footer?.text || 'Leftley Sports Club - Serving the community since 2016 with sports, activities, and a welcoming hub for all.',
          contactEmail: data.contact?.email || 'info@leftleysportsclub.org.uk',
          contactPhone: data.contact?.phone || '07940 464809',
          contactAddress: data.contact?.address || 'Leftley Community Hub, Leicester',
          socialLinks: data.contact?.socialMedia || { facebook: '', twitter: '', instagram: '', youtube: '' }
        });
        setPageContent({ title: 'Footer Content', content: '', description: '', images: [] });
      } else if (selectedPage === 'community-hub') {
        // Load community hub page settings
        response = await axios.get(`http://localhost:5000/api/pages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allData = response.data.data || response.data;
        const data = allData.communityHubPage || {};
        
        setCommunityHubData({
          hero: {
            title: data.hero?.title || 'Community Hub',
            subtitle: data.hero?.subtitle || 'Our community hub is the perfect venue for your events, meetings, and celebrations.',
            buttonText: data.hero?.buttonText || 'Book Now',
            secondaryButtonText: data.hero?.secondaryButtonText || 'View Gallery'
          },
          description: data.description || 'Our community hub is the perfect venue for your events, meetings, and celebrations. Modern facilities in the heart of the community.',
          features: data.features || [
            { icon: '🏠', title: 'Spacious Hall', description: 'Large open space perfect for events' },
            { icon: '🍽️', title: 'Kitchen Facilities', description: 'Fully equipped modern kitchen' },
            { icon: '🎵', title: 'Sound System', description: 'Professional audio equipment included' }
          ],
          pricing: {
            showPricing: data.pricing?.showPricing ?? true,
            depositAmount: data.pricing?.depositAmount || 200,
            currency: data.pricing?.currency || '£'
          },
          images: data.images || [],
          gallery: data.gallery || []
        });
        
        setPageContent({ title: 'Community Hub', content: '', description: '', images: [] });
      } else {
        // Load activity page
        response = await axios.get(`http://localhost:5000/api/pages/activity/${selectedPage}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data.data || response.data;
        setPageContent({
          title: data.title || '',
          content: data.content || '',
          description: data.description || '',
          heroImage: data.heroImage || '',
          images: data.images || [],
          schedule: {
            day: data.schedule?.day || '',
            time: data.schedule?.time || ''
          },
          location: data.location || '',
          details: data.details || '',
          contactEmail: data.contactEmail || '',
          contactPhone: data.contactPhone || ''
        });
      }
    } catch (error: any) {
      console.error('Error loading page:', error);
      setMessage('Error loading page content. Make sure you are logged in.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('adminToken');

      if (selectedPage === 'home') {
        // Save homepage settings
        await axios.put(
          `http://localhost:5000/api/homepage`,
          {
            hero: homepageData?.hero,
            stats: homepageData?.stats,
            sections: homepageData?.sections,
            contact: homepageData?.contact
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage('✅ Homepage saved successfully! All changes are now live on the website.');
      } else if (selectedPage === 'footer') {
        // Save footer settings with correct structure matching backend model
        await axios.put(
          `http://localhost:5000/api/homepage`,
          {
            footer: {
              text: footerContent.aboutText,
              showSocialMedia: true
            },
            contact: {
              email: footerContent.contactEmail,
              phone: footerContent.contactPhone,
              address: footerContent.contactAddress,
              socialMedia: {
                facebook: footerContent.socialLinks?.facebook || '',
                twitter: footerContent.socialLinks?.twitter || '',
                instagram: footerContent.socialLinks?.instagram || '',
                youtube: footerContent.socialLinks?.youtube || ''
              }
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage('✅ Footer saved successfully! Changes are now live on all pages of the website.');
      } else if (selectedPage === 'community-hub') {
        // Save community hub page settings
        await axios.put(
          `http://localhost:5000/api/pages/community-hub`,
          {
            hero: communityHubData?.hero,
            description: communityHubData?.description,
            features: communityHubData?.features,
            pricing: communityHubData?.pricing,
            gallery: communityHubData?.gallery
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage('✅ Community Hub page saved successfully! Changes are now live.');
      } else {
        // Save activity page
        await axios.put(
          `http://localhost:5000/api/pages/activity/${selectedPage}`,
          {
            title: pageContent.title,
            content: pageContent.content,
            description: pageContent.description,
            schedule: pageContent.schedule,
            location: pageContent.location,
            details: pageContent.details,
            contactEmail: pageContent.contactEmail,
            contactPhone: pageContent.contactPhone
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage('✅ Page saved successfully! Changes are now live.');
      }
      
      setTimeout(() => setMessage(''), 5000);
    } catch (error: any) {
      console.error('Error saving page:', error);
      setMessage('❌ Error saving page: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }

      await axios.post(
        `http://localhost:5000/api/pages/upload-images/${selectedPage}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setMessage('✅ Images uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
      loadPageContent(); // Reload to show new images
    } catch (error: any) {
      console.error('Error uploading images:', error);
      setMessage('❌ Error uploading images');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageUrl: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `http://localhost:5000/api/pages/delete-image/${selectedPage}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { imagePath: imageUrl }
        }
      );
      setMessage('✅ Image deleted successfully!');
      setTimeout(() => setMessage(''), 3000);
      loadPageContent(); // Reload to update images list
    } catch (error: any) {
      console.error('Error deleting image:', error);
      setMessage('❌ Error deleting image');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axios.post(
        `http://localhost:5000/api/homepage/upload-logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setMessage('✅ Logo uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
      
      // Update logo in state
      if (homepageData) {
        setHomepageData({
          ...homepageData,
          logo: response.data.data.logo
        });
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setMessage('❌ Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">� Complete Website Editor</h2>
        <p className="text-gray-600">Edit all website pages including homepage, activities, and global footer. Changes save to database and reflect immediately on the live website.</p>
      </div>

      {/* Page Selector */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3 text-gray-700">Select Page to Edit:</label>
        <select
          value={selectedPage}
          onChange={(e) => setSelectedPage(e.target.value)}
          className="w-full md:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-base font-medium"
          disabled={loading || saving}
        >
          <optgroup label="🏠 Main Pages">
            {pages.filter(p => p.category === 'main').map((page) => (
              <option key={page.value} value={page.value}>
                {page.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="🌐 Global Elements">
            {pages.filter(p => p.category === 'global').map((page) => (
              <option key={page.value} value={page.value}>
                {page.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="🎯 Activity Pages">
            {pages.filter(p => p.category === 'activity').map((page) => (
              <option key={page.value} value={page.value}>
                {page.label}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg font-medium ${
          message.includes('❌') || message.includes('Error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading page content...</p>
        </div>
      ) : (
        <div className="space-y-6">
          
          {/* FOOTER EDITOR */}
          {selectedPage === 'footer' && footerContent && (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-blue-900 mb-2">📋 Global Footer Content</h3>
                <p className="text-blue-700">This footer appears on all pages of the website. Edit contact information and social links below.</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">About Text</label>
                <textarea
                  value={footerContent.aboutText || ''}
                  onChange={(e) => setFooterContent({ ...footerContent, aboutText: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="Brief description about Leftley Sports Club..."
                />
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold mb-4 text-gray-700">Contact Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">Email</label>
                    <input
                      type="email"
                      value={footerContent.contactEmail || ''}
                      onChange={(e) => setFooterContent({ ...footerContent, contactEmail: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="contact@leftleysportsclub.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">Phone</label>
                    <input
                      type="tel"
                      value={footerContent.contactPhone || ''}
                      onChange={(e) => setFooterContent({ ...footerContent, contactPhone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0116 xxx xxxx"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">Address</label>
                    <input
                      type="text"
                      value={footerContent.contactAddress || ''}
                      onChange={(e) => setFooterContent({ ...footerContent, contactAddress: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Leftley Sports Club, Leicester"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold mb-4 text-gray-700">Social Media Links</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">📘 Facebook URL</label>
                    <input
                      type="url"
                      value={footerContent.socialLinks?.facebook || ''}
                      onChange={(e) => setFooterContent({ 
                        ...footerContent, 
                        socialLinks: { ...footerContent.socialLinks, facebook: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">🐦 Twitter URL</label>
                    <input
                      type="url"
                      value={footerContent.socialLinks?.twitter || ''}
                      onChange={(e) => setFooterContent({ 
                        ...footerContent, 
                        socialLinks: { ...footerContent.socialLinks, twitter: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">📷 Instagram URL</label>
                    <input
                      type="url"
                      value={footerContent.socialLinks?.instagram || ''}
                      onChange={(e) => setFooterContent({ 
                        ...footerContent, 
                        socialLinks: { ...footerContent.socialLinks, instagram: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-2 text-gray-600">📺 YouTube URL</label>
                    <input
                      type="url"
                      value={footerContent.socialLinks?.youtube || ''}
                      onChange={(e) => setFooterContent({ 
                        ...footerContent, 
                        socialLinks: { ...footerContent.socialLinks, youtube: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Preview of Footer Data */}
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold mb-4 text-gray-700">📋 Footer Preview</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">About: </span>
                    <span className="text-gray-800">{footerContent.aboutText || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Email: </span>
                    <span className="text-gray-800">{footerContent.contactEmail || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Phone: </span>
                    <span className="text-gray-800">{footerContent.contactPhone || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Address: </span>
                    <span className="text-gray-800">{footerContent.contactAddress || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Social Links: </span>
                    <span className="text-gray-800">
                      {footerContent.socialLinks?.facebook && '📘 '} 
                      {footerContent.socialLinks?.twitter && '🐦 '} 
                      {footerContent.socialLinks?.instagram && '📷 '}
                      {footerContent.socialLinks?.youtube && '📺 '}
                      {!footerContent.socialLinks?.facebook && !footerContent.socialLinks?.twitter && 
                       !footerContent.socialLinks?.instagram && !footerContent.socialLinks?.youtube && 'None set'}
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-xs text-gray-500 italic">
                  This is how your footer data will be saved. Click "Save All Changes" to update the website.
                </p>
              </div>
            </>
          )}

          {/* HOMEPAGE EDITOR */}
          {selectedPage === 'home' && homepageData && (
            <>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-green-900 mb-2">🏠 Complete Homepage Editor</h3>
                <p className="text-green-700">Edit all sections of the homepage including hero, stats, membership, hub, and activities.</p>
              </div>

              {/* Contact Address */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">📍 Club Address (Top Bar)</label>
                <input
                  type="text"
                  value={homepageData.contact?.address || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    contact: { ...homepageData.contact, address: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="LSC Community Hub, Martin Peters Court, Meadow Rd, Barking IG11 9FE"
                />
              </div>

              {/* Logo Section */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">🎨 Club Logo</label>
                <div className="space-y-4">
                  {homepageData.logo && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Current Logo:</p>
                      <img
                        src={`http://localhost:5000${homepageData.logo}`}
                        alt="Club Logo"
                        className="h-20 object-contain border border-gray-300 rounded-lg p-2 bg-white"
                      />
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer 
                        ${uploadingLogo ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {uploadingLogo ? 'Uploading...' : 'Upload New Logo'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Hero Section */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                <h4 className="font-bold text-blue-900 mb-1">1️⃣ Hero Section</h4>
                <p className="text-sm text-blue-700">Main banner at the top of the homepage</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hero Title (Main Heading)</label>
                <input
                  type="text"
                  value={homepageData.hero?.title || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    hero: { ...homepageData.hero!, title: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-2xl font-bold"
                  placeholder="Welcome to Leftley Sports Club"
                />
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hero Subtitle</label>
                <input
                  type="text"
                  value={homepageData.hero?.subtitle || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    hero: { ...homepageData.hero!, subtitle: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                  placeholder="Building a stronger community through sports and activities since 2016"
                />
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-4 text-gray-700">Hero Button Text</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Primary Button</label>
                    <input
                      type="text"
                      value={homepageData.hero?.buttonText || ''}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        hero: { ...homepageData.hero!, buttonText: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Become a Member"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Secondary Button</label>
                    <input
                      type="text"
                      value={homepageData.hero?.secondaryButtonText || ''}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        hero: { ...homepageData.hero!, secondaryButtonText: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Hire the Hub"
                    />
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4 mt-6">
                <h4 className="font-bold text-purple-900 mb-1">3️⃣ Stats Section</h4>
                <p className="text-sm text-purple-700">Statistics displayed below the hero section</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-4 text-gray-700">Statistics</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Active Members</label>
                    <input
                      type="text"
                      value={homepageData.stats?.members || ''}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        stats: { ...homepageData.stats!, members: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="500+"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Sports & Activities</label>
                    <input
                      type="text"
                      value={homepageData.stats?.activities || ''}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        stats: { ...homepageData.stats!, activities: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="5+"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Year Established</label>
                    <input
                      type="text"
                      value={homepageData.stats?.yearEstablished || ''}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        stats: { ...homepageData.stats!, yearEstablished: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Since 2016"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Hub Capacity</label>
                    <input
                      type="text"
                      value={homepageData.stats?.hubCapacity || ''}
                      onChange={(e) => setHomepageData({
                        ...homepageData,
                        stats: { ...homepageData.stats!, hubCapacity: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="80"
                    />
                  </div>
                </div>
              </div>

              {/* Membership Section */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4 mt-6">
                <h4 className="font-bold text-green-900 mb-1">4️⃣ Membership Section</h4>
                <p className="text-sm text-green-700">"Become a Member" card content</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Membership Heading</label>
                <input
                  type="text"
                  value={homepageData.sections?.membershipHeading || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    sections: { ...homepageData.sections!, membershipHeading: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                  placeholder="Become a Member"
                />
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Membership Description</label>
                <textarea
                  value={homepageData.sections?.membershipText || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    sections: { ...homepageData.sections!, membershipText: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Join our community of 500+ members and get access to all our sports and activities."
                />
              </div>

              {/* Hub Section */}
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4 mt-6">
                <h4 className="font-bold text-orange-900 mb-1">4️⃣ Community Hub Section</h4>
                <p className="text-sm text-orange-700">"Hire Our Community Hub" card content</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hub Heading</label>
                <input
                  type="text"
                  value={homepageData.sections?.hubHeading || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    sections: { ...homepageData.sections!, hubHeading: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                  placeholder="Hire Our Community Hub"
                />
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hub Description</label>
                <textarea
                  value={homepageData.sections?.hubText || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    sections: { ...homepageData.sections!, hubText: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Our community hub is perfect for events, meetings, and celebrations. Modern facilities accommodating up to 80 guests."
                />
              </div>

              {/* Activities Section */}
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4 mt-6">
                <h4 className="font-bold text-indigo-900 mb-1">5️⃣ Activities Section</h4>
                <p className="text-sm text-indigo-700">Section heading for activities showcase</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Activities Section Heading</label>
                <input
                  type="text"
                  value={homepageData.sections?.activitiesHeading || ''}
                  onChange={(e) => setHomepageData({
                    ...homepageData,
                    sections: { ...homepageData.sections!, activitiesHeading: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-bold text-xl"
                  placeholder="Our Activities"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Note: Individual activity cards (Football, Walk & Talk, etc.) can be edited in their respective activity page editors.
                </p>
              </div>
            </>
          )}

          {/* COMMUNITY HUB PAGE EDITOR */}
          {selectedPage === 'community-hub' && communityHubData && (
            <>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-green-900 mb-2">🏢 Community Hub Page Settings</h3>
                <p className="text-green-700">Edit all aspects of the Community Hub page including hero section, description, features, and pricing.</p>
              </div>

              {/* Hero Section */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4 mt-6">
                <h4 className="font-bold text-blue-900 mb-1">1️⃣ Hero Section</h4>
                <p className="text-sm text-blue-700">Main banner at the top of the Community Hub page</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hero Title</label>
                <input
                  type="text"
                  value={communityHubData.hero?.title || ''}
                  onChange={(e) => setCommunityHubData({
                    ...communityHubData,
                    hero: { ...communityHubData.hero!, title: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-bold text-2xl"
                  placeholder="Community Hub"
                />
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hero Subtitle</label>
                <textarea
                  value={communityHubData.hero?.subtitle || ''}
                  onChange={(e) => setCommunityHubData({
                    ...communityHubData,
                    hero: { ...communityHubData.hero!, subtitle: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="Our community hub is the perfect venue for your events"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Primary Button Text</label>
                  <input
                    type="text"
                    value={communityHubData.hero?.buttonText || ''}
                    onChange={(e) => setCommunityHubData({
                      ...communityHubData,
                      hero: { ...communityHubData.hero!, buttonText: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Book Now"
                  />
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Secondary Button Text</label>
                  <input
                    type="text"
                    value={communityHubData.hero?.secondaryButtonText || ''}
                    onChange={(e) => setCommunityHubData({
                      ...communityHubData,
                      hero: { ...communityHubData.hero!, secondaryButtonText: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="View Gallery"
                  />
                </div>
              </div>

              {/* Description Section */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4 mt-6">
                <h4 className="font-bold text-purple-900 mb-1">2️⃣ Page Description</h4>
                <p className="text-sm text-purple-700">Main description text for the hub</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Description</label>
                <textarea
                  value={communityHubData.description || ''}
                  onChange={(e) => setCommunityHubData({
                    ...communityHubData,
                    description: e.target.value
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows={4}
                  placeholder="Our community hub is the perfect venue for your events, meetings, and celebrations..."
                />
              </div>

              {/* Features Section */}
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4 mt-6">
                <h4 className="font-bold text-orange-900 mb-1">3️⃣ Hub Features</h4>
                <p className="text-sm text-orange-700">Key features and amenities of the hub</p>
              </div>

              {communityHubData.features?.map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-semibold text-gray-800">Feature {index + 1}</h5>
                    <button
                      onClick={() => {
                        const newFeatures = communityHubData.features!.filter((_, i) => i !== index);
                        setCommunityHubData({ ...communityHubData, features: newFeatures });
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      🗑️ Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-2">Icon (emoji or text)</label>
                      <input
                        type="text"
                        value={feature.icon}
                        onChange={(e) => {
                          const newFeatures = [...communityHubData.features!];
                          newFeatures[index].icon = e.target.value;
                          setCommunityHubData({ ...communityHubData, features: newFeatures });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="🏠"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-2">Feature Title</label>
                      <input
                        type="text"
                        value={feature.title}
                        onChange={(e) => {
                          const newFeatures = [...communityHubData.features!];
                          newFeatures[index].title = e.target.value;
                          setCommunityHubData({ ...communityHubData, features: newFeatures });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Spacious Hall"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-2">Feature Description</label>
                      <textarea
                        value={feature.description}
                        onChange={(e) => {
                          const newFeatures = [...communityHubData.features!];
                          newFeatures[index].description = e.target.value;
                          setCommunityHubData({ ...communityHubData, features: newFeatures });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={2}
                        placeholder="Large open space perfect for events"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={() => {
                  const newFeatures = [
                    ...(communityHubData.features || []),
                    { icon: '✨', title: 'New Feature', description: 'Feature description' }
                  ];
                  setCommunityHubData({ ...communityHubData, features: newFeatures });
                }}
                className="w-full bg-green-50 border-2 border-dashed border-green-300 text-green-700 py-4 px-6 rounded-lg hover:bg-green-100 transition-colors font-semibold"
              >
                ➕ Add New Feature
              </button>

              {/* Pricing Section */}
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4 mt-6">
                <h4 className="font-bold text-indigo-900 mb-1">4️⃣ Pricing Information</h4>
                <p className="text-sm text-indigo-700">Deposit and pricing display settings</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="showPricing"
                    checked={communityHubData.pricing?.showPricing ?? true}
                    onChange={(e) => setCommunityHubData({
                      ...communityHubData,
                      pricing: { ...communityHubData.pricing!, showPricing: e.target.checked }
                    })}
                    className="mr-3 h-5 w-5"
                  />
                  <label htmlFor="showPricing" className="text-sm font-semibold text-gray-700">
                    Show Pricing on Page
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Currency Symbol</label>
                    <input
                      type="text"
                      value={communityHubData.pricing?.currency || '£'}
                      onChange={(e) => setCommunityHubData({
                        ...communityHubData,
                        pricing: { ...communityHubData.pricing!, currency: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="£"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Deposit Amount</label>
                    <input
                      type="number"
                      value={communityHubData.pricing?.depositAmount || 0}
                      onChange={(e) => setCommunityHubData({
                        ...communityHubData,
                        pricing: { ...communityHubData.pricing!, depositAmount: parseInt(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="200"
                    />
                  </div>
                </div>
              </div>

              {/* Image Gallery Section */}
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200 mb-4 mt-6">
                <h4 className="font-bold text-teal-900 mb-1">5️⃣ Hub Images & Gallery</h4>
                <p className="text-sm text-teal-700">Upload multiple images for the Community Hub page and gallery</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Hub Images</label>
            
                {/* Upload Button */}
                <div className="mb-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="hidden"
                    id="hub-image-upload"
                  />
                  <label
                    htmlFor="hub-image-upload"
                    className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer 
                      ${uploadingImage ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {uploadingImage ? 'Uploading...' : 'Upload Images'}
                  </label>
                  <span className="ml-3 text-sm text-gray-500">Select one or more images</span>
                </div>

                {/* Current Images */}
                {communityHubData.images && communityHubData.images.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-medium mb-3 text-gray-700">Current Images ({communityHubData.images.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {communityHubData.images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={`http://localhost:5000${img}`}
                            alt={`Hub image ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            onClick={() => handleDeleteImage(img)}
                            className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                            title="Delete image"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No images uploaded yet</p>
                )}
              </div>
            </>
          )}

          {/* ACTIVITY PAGES EDITOR */}
          {selectedPage !== 'home' && selectedPage !== 'footer' && selectedPage !== 'community-hub' && (
            <>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-purple-900 mb-2">🎯 Activity Page Content</h3>
                <p className="text-purple-700">Edit the full page content for this activity including title, description, and details.</p>
              </div>

              {/* Title Field */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Page Title</label>
                <input
                  type="text"
                  value={pageContent.title}
                  onChange={(e) => setPageContent({ ...pageContent, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xl font-bold"
                  placeholder="Enter page title (e.g., Football Training Sessions)"
                />
              </div>

              {/* Description Field */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Page Description / Subtitle</label>
                <input
                  type="text"
                  value={pageContent.description || ''}
                  onChange={(e) => setPageContent({ ...pageContent, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter a short description or subtitle"
                />
              </div>

              {/* Schedule Section */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-4 text-gray-700">📅 Schedule</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Day</label>
                    <input
                      type="text"
                      value={pageContent.schedule?.day || ''}
                      onChange={(e) => setPageContent({ 
                        ...pageContent, 
                        schedule: { ...pageContent.schedule!, day: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Every Monday"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Time</label>
                    <input
                      type="text"
                      value={pageContent.schedule?.time || ''}
                      onChange={(e) => setPageContent({ 
                        ...pageContent, 
                        schedule: { ...pageContent.schedule!, time: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 6:00 PM - 8:00 PM"
                    />
                  </div>
                </div>
              </div>

              {/* Location Field */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">📍 Location</label>
                <input
                  type="text"
                  value={pageContent.location || ''}
                  onChange={(e) => setPageContent({ ...pageContent, location: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Community Hub Main Hall"
                />
              </div>

              {/* Details Field */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">📝 Details</label>
                <textarea
                  value={pageContent.details || ''}
                  onChange={(e) => setPageContent({ ...pageContent, details: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="Enter additional details, requirements, costs, etc."
                />
              </div>

              {/* Contact Information */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-4 text-gray-700">📞 Contact Information (Optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Contact Email</label>
                    <input
                      type="email"
                      value={pageContent.contactEmail || ''}
                      onChange={(e) => setPageContent({ ...pageContent, contactEmail: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="activity@leftleysportsclub.org.uk"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-2">Contact Phone</label>
                    <input
                      type="tel"
                      value={pageContent.contactPhone || ''}
                      onChange={(e) => setPageContent({ ...pageContent, contactPhone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="07940 464809"
                    />
                  </div>
                </div>
              </div>

              {/* Content Field with Rich Text Editor */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Page Content</label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={pageContent.content}
                    onChange={(value) => setPageContent({ ...pageContent, content: value })}
                    modules={quillModules}
                    className="bg-white"
                    style={{ minHeight: '400px' }}
                    placeholder="Write your page content here. Use the toolbar above for formatting..."
                  />
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Character count: {pageContent.content.replace(/<[^>]*>/g, '').length}
                </div>
              </div>

              {/* Image Upload Section for Activity Pages */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold mb-3 text-gray-700">Page Images</label>
            
            {/* Upload Button */}
            <div className="mb-4">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer 
                  ${uploadingImage ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {uploadingImage ? 'Uploading...' : 'Upload Images'}
              </label>
              <span className="ml-3 text-sm text-gray-500">Select one or more images</span>
            </div>

            {/* Current Images */}
            {pageContent.images && pageContent.images.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium mb-3 text-gray-700">Current Images ({pageContent.images.length})</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {pageContent.images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={`http://localhost:5000${img}`}
                        alt={`Page image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        onClick={() => handleDeleteImage(img)}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                        title="Delete image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No images uploaded yet</p>
            )}
              </div>

              {/* Hero Image Section */}
              {pageContent.heroImage && (
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold mb-3 text-gray-700">Hero Image</label>
                  <div className="relative inline-block">
                    <img
                      src={`http://localhost:5000${pageContent.heroImage}`}
                      alt="Hero"
                      className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving || uploadingImage}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 
                disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md"
            >
              {saving ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Saving...
                </>
              ) : (
                '💾 Save All Changes'
              )}
            </button>
            <button
              onClick={loadPageContent}
              disabled={loading || saving || uploadingImage}
              className="px-8 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 
                disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md"
            >
              🔄 Reset Changes
            </button>
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p className="font-semibold mb-2">💡 Editor Tips:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Use the rich text editor toolbar for formatting (bold, lists, colors, etc.)</li>
              <li>Upload multiple images at once - they'll appear in a gallery on the page</li>
              <li>Changes are saved to the database and appear immediately on the public website</li>
              <li>Click "Reset Changes" to reload the current saved version</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageEditor;
